import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, Users, UserPlus, UserMinus, Music, Heart, MessageCircle,
  Share2, MapPin, Globe, Instagram, Play, Pause, ChevronRight,
  MoreHorizontal, Trash2, Send, ExternalLink, Calendar,
  Star, Award, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const SocialProfile = () => {
  const { userId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [isPlayingSong, setIsPlayingSong] = useState(false);
  const audioRef = useRef(null);

  const targetId = userId || user?.id;

  useEffect(() => {
    if (targetId) fetchProfile();
  }, [targetId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/social/profiles/${targetId}`, { headers });
      setProfileData(res.data);
      setIsFollowing(res.data.is_following);
      setFollowersCount(res.data.followers_count);
      setFollowingCount(res.data.following_count);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Profile not found');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!token) { toast.error('Please log in to follow'); return; }
    try {
      if (isFollowing) {
        await axios.delete(`${API}/social/follow/${targetId}`, { headers: { Authorization: `Bearer ${token}` } });
        setIsFollowing(false);
        setFollowersCount(c => c - 1);
      } else {
        await axios.post(`${API}/social/follow/${targetId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsFollowing(true);
        setFollowersCount(c => c + 1);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const fetchFollowers = async () => {
    const res = await axios.get(`${API}/social/followers/${targetId}`);
    setFollowersList(res.data);
    setShowFollowers(true);
  };

  const fetchFollowing = async () => {
    const res = await axios.get(`${API}/social/following/${targetId}`);
    setFollowingList(res.data);
    setShowFollowing(true);
  };

  const toggleProfileSong = () => {
    if (!audioRef.current) return;
    if (isPlayingSong) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlayingSong(!isPlayingSong);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 pb-24" data-testid="social-profile-loading">
        <Skeleton className="h-48 w-full rounded-xl" />
        <div className="flex gap-4">
          <Skeleton className="w-32 h-32 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (!profileData) return null;

  const { user: profileUser, profile, profile_song, top_friends, linked_data, posts, visitor_count, badges } = profileData;
  const isOwnProfile = user?.id === profileUser.id;
  const themeColors = {
    primary: profile?.theme_primary_color || '#8B5CF6',
    secondary: profile?.theme_secondary_color || '#EC4899',
    bg: profile?.theme_bg_color || '#0F0F23',
    text: profile?.theme_text_color || '#E5E7EB',
  };
  const displayName = profile?.display_name || profileUser.name || 'Anonymous';
  const roleBadge = profileUser.role === 'user' ? 'Member' : profileUser.role?.replace('-', ' ');

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="social-profile-page">
      {/* Cover + Profile Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative rounded-xl overflow-hidden border border-border mb-6"
        style={{ background: `linear-gradient(135deg, ${themeColors.bg}, ${themeColors.primary}22, ${themeColors.secondary}15)` }}
      >
        {/* Cover Photo */}
        <div className="h-36 sm:h-48 relative overflow-hidden">
          {profile?.cover_photo ? (
            <img src={profile.cover_photo} alt="Cover" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full"
              style={{ background: `linear-gradient(135deg, ${themeColors.primary}40, ${themeColors.secondary}30, ${themeColors.bg})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />

          {/* Visitor Counter - MySpace style */}
          {(profile?.show_visitor_count !== false) && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: 'spring' }}
              className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-sm border border-white/10"
              data-testid="visitor-counter"
            >
              <Eye size={14} className="text-purple-400" />
              <span className="text-xs font-mono text-purple-300">{(visitor_count || 0).toLocaleString()} views</span>
            </motion.div>
          )}
        </div>

        {/* Profile Info Overlay */}
        <div className="relative px-4 sm:px-6 pb-5 -mt-16">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div
                className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 shadow-xl"
                style={{ borderColor: themeColors.primary }}
              >
                {profile?.profile_pic ? (
                  <img src={profile.profile_pic} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-card text-4xl font-bold" style={{ color: themeColors.primary }}>
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              {/* Online indicator */}
              <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-black" />
            </div>

            {/* Name + role */}
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="text-2xl sm:text-3xl font-bold truncate" style={{ color: themeColors.text }}>
                {displayName}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className="px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize"
                  style={{ background: `${themeColors.primary}30`, color: themeColors.primary }}
                  data-testid="profile-role-badge"
                >
                  {roleBadge}
                </span>
                {badges && badges.map(badge => (
                  <BadgeChip key={badge.key} badge={badge} />
                ))}
                {profile?.location && (
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <MapPin size={12} /> {profile.location}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 shrink-0">
              {isOwnProfile ? (
                <Link to="/profile/edit">
                  <Button size="sm" variant="outline" className="border-purple-500/50 text-purple-400" data-testid="edit-profile-btn">
                    Edit Profile
                  </Button>
                </Link>
              ) : (
                <Button
                  size="sm"
                  onClick={handleFollow}
                  className={isFollowing ? 'bg-gray-700 hover:bg-gray-600' : ''}
                  style={!isFollowing ? { background: themeColors.primary } : {}}
                  data-testid="follow-btn"
                >
                  {isFollowing ? <><UserMinus size={14} className="mr-1" /> Unfollow</> : <><UserPlus size={14} className="mr-1" /> Follow</>}
                </Button>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-6 mt-4">
            <button onClick={fetchFollowers} className="text-center hover:opacity-80 transition" data-testid="followers-count">
              <span className="block text-lg font-bold" style={{ color: themeColors.text }}>{followersCount}</span>
              <span className="text-xs text-gray-500">Followers</span>
            </button>
            <button onClick={fetchFollowing} className="text-center hover:opacity-80 transition" data-testid="following-count">
              <span className="block text-lg font-bold" style={{ color: themeColors.text }}>{followingCount}</span>
              <span className="text-xs text-gray-500">Following</span>
            </button>
            <div className="text-center">
              <span className="block text-lg font-bold" style={{ color: themeColors.text }}>{posts?.length || 0}</span>
              <span className="text-xs text-gray-500">Posts</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Two-column MySpace Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN - Bio, Song, Top Friends, Links */}
        <div className="space-y-4">
          {/* Bio */}
          {profile?.bio && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-border bg-card p-4"
            >
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">About</h3>
              <p className="text-sm leading-relaxed" style={{ color: themeColors.text }}>{profile.bio}</p>
            </motion.div>
          )}

          {/* Profile Song - MySpace Feature */}
          {profile_song && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-xl border border-border bg-card overflow-hidden"
              data-testid="profile-song-player"
            >
              <div className="p-3 flex items-center gap-3">
                <button
                  onClick={toggleProfileSong}
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all"
                  style={{ background: `${themeColors.primary}30` }}
                  data-testid="profile-song-play-btn"
                >
                  {isPlayingSong ? <Pause size={18} style={{ color: themeColors.primary }} /> : <Play size={18} className="ml-0.5" style={{ color: themeColors.primary }} />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Profile Song</p>
                  <p className="text-sm font-semibold truncate" style={{ color: themeColors.text }}>{profile_song.title}</p>
                  <p className="text-xs text-gray-400 truncate">{profile_song.artist}</p>
                </div>
                <Music size={16} style={{ color: themeColors.primary }} />
              </div>
              {/* Hidden audio element */}
              <audio ref={audioRef} src={profile_song.audio_url} onEnded={() => setIsPlayingSong(false)} />
              {/* Visualizer bar */}
              {isPlayingSong && (
                <div className="h-1 w-full flex gap-px">
                  {Array.from({ length: 40 }).map((_, i) => (
                    <motion.div
                      key={i}
                      className="flex-1"
                      style={{ background: themeColors.primary }}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 0.5 + Math.random() * 0.5, repeat: Infinity, delay: Math.random() * 0.3 }}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Top Friends - MySpace Feature */}
          {top_friends && top_friends.length > 0 && (profile?.show_top_friends !== false) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-xl border border-border bg-card p-4"
              data-testid="top-friends-section"
            >
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Top Friends ({top_friends.length})
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {top_friends.map((friend) => (
                  <Link
                    key={friend.id}
                    to={`/u/${friend.id}`}
                    className="text-center group"
                    data-testid={`top-friend-${friend.id}`}
                  >
                    <div className="w-full aspect-square rounded-lg overflow-hidden border border-border group-hover:border-purple-500/50 transition mb-1">
                      {friend.profile_pic ? (
                        <img src={friend.profile_pic} alt={friend.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-card text-lg font-bold text-gray-500">
                          {friend.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 truncate group-hover:text-purple-400 transition">{friend.name}</p>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Social Links */}
          {(profile?.instagram || profile?.twitter || profile?.website || profile?.spotify) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-xl border border-border bg-card p-4 space-y-2"
            >
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Links</h3>
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300">
                  <Globe size={14} /> Website <ExternalLink size={10} />
                </a>
              )}
              {profile.instagram && (
                <a href={`https://instagram.com/${profile.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-pink-400 hover:text-pink-300">
                  <Instagram size={14} /> @{profile.instagram.replace('@', '')}
                </a>
              )}
              {profile.spotify && (
                <a href={profile.spotify} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300">
                  <Music size={14} /> Spotify <ExternalLink size={10} />
                </a>
              )}
            </motion.div>
          )}
        </div>

        {/* RIGHT COLUMN - Posts Feed */}
        <div className="lg:col-span-2 space-y-4">
          {/* Post Creation - only for non-regular users on own profile */}
          {isOwnProfile && profileUser.role !== 'user' && (
            <CreatePostBox token={token} user={user} themeColors={themeColors} onPost={fetchProfile} />
          )}

          {/* Reshare hint for regular users */}
          {isOwnProfile && profileUser.role === 'user' && (
            <div className="rounded-xl border border-border bg-card/50 p-4 text-center">
              <p className="text-sm text-gray-400">Browse the <Link to="/feed" className="text-purple-400 hover:underline">feed</Link> to reshare content from local artists and businesses!</p>
            </div>
          )}

          {/* Posts */}
          {posts && posts.length > 0 ? (
            posts.map((post, index) => (
              <ProfilePostCard
                key={post.id}
                post={post}
                user={user}
                token={token}
                themeColors={themeColors}
                index={index}
                onDelete={fetchProfile}
              />
            ))
          ) : (
            <div className="text-center py-12 rounded-xl border border-border bg-card/30">
              <Music size={40} className="mx-auto mb-3 text-gray-600" />
              <p className="text-gray-500">No posts yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Followers/Following Modal */}
      <AnimatePresence>
        {(showFollowers || showFollowing) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
            onClick={() => { setShowFollowers(false); setShowFollowing(false); }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-card border border-border rounded-xl p-5 max-w-sm w-full max-h-[60vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="followers-modal"
            >
              <h3 className="font-bold text-lg mb-4">{showFollowers ? 'Followers' : 'Following'}</h3>
              {(showFollowers ? followersList : followingList).map(u => (
                <Link
                  key={u.id}
                  to={`/u/${u.id}`}
                  className="flex items-center gap-3 py-2 hover:bg-white/5 rounded-lg px-2 transition"
                  onClick={() => { setShowFollowers(false); setShowFollowing(false); }}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={u.profile_pic} />
                    <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-sm">{u.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{u.role}</p>
                  </div>
                </Link>
              ))}
              {(showFollowers ? followersList : followingList).length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">None yet</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Post creation box
const CreatePostBox = ({ token, user, themeColors, onPost }) => {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('text');
  const [imageUrl, setImageUrl] = useState('');
  const [posting, setPosting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/social/posts`, {
        content,
        post_type: postType,
        image_url: imageUrl || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setContent('');
      setImageUrl('');
      toast.success('Post published!');
      onPost();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border bg-card p-4 space-y-3"
      data-testid="create-post-box"
    >
      <div className="flex items-center gap-3">
        <Avatar className="w-9 h-9">
          <AvatarFallback style={{ background: `${themeColors.primary}30`, color: themeColors.primary }}>
            {user?.name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Share something with your followers..."
          className="flex-1 bg-transparent border-none outline-none resize-none text-sm placeholder:text-gray-500"
          rows={2}
          data-testid="post-content-input"
        />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-border/50">
        <div className="flex gap-2">
          {['text', 'photo', 'event', 'announcement'].map(type => (
            <button
              key={type}
              onClick={() => setPostType(type)}
              className={`px-2.5 py-1 rounded-full text-xs capitalize transition ${postType === type ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-gray-300'}`}
              data-testid={`post-type-${type}`}
            >
              {type}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!content.trim() || posting}
          style={{ background: themeColors.primary }}
          data-testid="submit-post-btn"
        >
          <Send size={14} className="mr-1" /> Post
        </Button>
      </div>
      {postType === 'photo' && (
        <Input
          value={imageUrl}
          onChange={e => setImageUrl(e.target.value)}
          placeholder="Image URL..."
          className="bg-white/5 border-white/10 text-sm"
          data-testid="post-image-url-input"
        />
      )}
    </motion.div>
  );
};

// Individual post card
const ProfilePostCard = ({ post, user, token, themeColors, index, onDelete }) => {
  const [liked, setLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  const handleLike = async () => {
    if (!token) { toast.error('Please log in'); return; }
    try {
      const res = await axios.post(`${API}/social/posts/${post.id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
    } catch {
      toast.error('Failed to like');
    }
  };

  const handleShare = async () => {
    if (!token) { toast.error('Please log in to share'); return; }
    try {
      await axios.post(`${API}/social/posts/${post.id}/share`, { post_id: post.id }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Shared to your feed!');
    } catch (err) {
      toast.error('Failed to share');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !token) return;
    try {
      const res = await axios.post(`${API}/social/posts/${post.id}/comments`, { content: newComment }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch {
      toast.error('Failed to comment');
    }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/social/posts/${post.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setComments(res.data.comments || []);
    } catch { }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/social/posts/${post.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Post deleted');
      onDelete();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const isOwner = user?.id === post.author_id || user?.role === 'admin';
  const timeAgo = getTimeAgo(post.created_at);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="rounded-xl border border-border bg-card overflow-hidden"
      data-testid={`profile-post-${post.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <Avatar className="w-8 h-8">
            <AvatarImage src={post.author_profile_pic} />
            <AvatarFallback className="text-xs">{post.author_display_name?.charAt(0) || post.author_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-semibold">{post.author_display_name || post.author_name}</p>
            <p className="text-[10px] text-gray-500">{timeAgo} · {post.post_type}</p>
          </div>
        </div>
        {isOwner && (
          <button onClick={handleDelete} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition" data-testid={`delete-post-${post.id}`}>
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-3 pb-2">
        <p className="text-sm whitespace-pre-wrap">{post.content}</p>
      </div>

      {/* Image */}
      {post.image_url && (
        <div className="px-3 pb-2">
          <img src={post.image_url} alt="" className="w-full rounded-lg max-h-96 object-cover" />
        </div>
      )}

      {/* Event Info */}
      {post.post_type === 'event' && post.event_date && (
        <div className="mx-3 mb-2 px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center gap-2">
          <Calendar size={14} className="text-purple-400" />
          <span className="text-xs text-purple-300">{post.event_date} {post.event_venue && `at ${post.event_venue}`}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 px-3 pb-3 pt-1 border-t border-border/30">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition ${liked ? 'text-red-400 bg-red-500/10' : 'text-gray-500 hover:text-red-400 hover:bg-red-500/5'}`}
          data-testid={`like-btn-${post.id}`}
        >
          <Heart size={14} fill={liked ? 'currentColor' : 'none'} /> {likesCount}
        </button>
        <button
          onClick={() => { if (!showComments) fetchComments(); setShowComments(!showComments); }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-blue-400 hover:bg-blue-500/5 transition"
          data-testid={`comment-toggle-${post.id}`}
        >
          <MessageCircle size={14} /> {post.comments_count || 0}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-green-400 hover:bg-green-500/5 transition"
          data-testid={`share-btn-${post.id}`}
        >
          <Share2 size={14} /> {post.shares_count || 0}
        </button>
      </div>

      {/* Comments */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-border/50 overflow-hidden"
          >
            <div className="p-3 space-y-3">
              <form onSubmit={handleComment} className="flex gap-2">
                <Input
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 bg-white/5 border-white/10 text-sm h-8"
                  data-testid={`comment-input-${post.id}`}
                />
                <Button type="submit" size="sm" disabled={!newComment.trim()} className="h-8 px-3" data-testid={`comment-submit-${post.id}`}>
                  <Send size={12} />
                </Button>
              </form>
              {comments.map(c => (
                <div key={c.id} className="flex gap-2">
                  <Avatar className="w-6 h-6 shrink-0">
                    <AvatarImage src={c.author_profile_pic} />
                    <AvatarFallback className="text-[10px]">{c.author_display_name?.charAt(0) || c.author_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-xs">
                      <span className="font-semibold mr-1">{c.author_display_name || c.author_name}</span>
                      {c.content}
                    </p>
                    <p className="text-[10px] text-gray-600 mt-0.5">{getTimeAgo(c.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const BADGE_ICONS = {
  star: Star,
  award: Award,
  shield: Shield,
  music: Music,
};

const BadgeChip = ({ badge }) => {
  const Icon = BADGE_ICONS[badge.icon] || Star;
  return (
    <motion.span
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border"
      style={{
        background: `${badge.color}18`,
        color: badge.color,
        borderColor: `${badge.color}40`,
      }}
      data-testid={`badge-${badge.key}`}
    >
      <Icon size={10} />
      {badge.label}
    </motion.span>
  );
};

export default SocialProfile;
